/**
 * resolveAddressDisplay
 *
 * Best-effort coercion of a customer's stored service address into a single
 * human-readable string for display.
 *
 * Across the app, addresses have been stored in three different shapes
 * depending on which surface created the lead:
 *
 *   - String:                       "123 Main St, Akron, OH 44256"
 *   - AddClientModal object:        { line1, city, state, zip } + addressDisplay
 *   - Legacy helpers schema:        { streetNumber, streetName, city, state, zip }
 *   - This module's earlier guess:  { street, city, state, zip }
 *
 * Resolution order:
 *   1. `lead.addressDisplay` (already a flat, correctly-ordered string)
 *   2. `lead.address` if string
 *   3. `lead.address` if object — join street + city + state + zip,
 *      handling all known street-key spellings (line1 / street /
 *      streetNumber+streetName)
 *
 * Returns '' (empty string) when no usable data is present, so callers
 * can keep their `hasAddress = !!resolveAddressDisplay(client).trim()`
 * checks straightforward.
 */
export function resolveAddressDisplay(leadOrClient) {
  if (!leadOrClient) return '';

  // 1. Flat mirror produced by AddClientModal — always preferred when present.
  const flat = leadOrClient.addressDisplay;
  if (typeof flat === 'string' && flat.trim()) return flat.trim();

  const raw = leadOrClient.address;

  // 2. Plain string address.
  if (typeof raw === 'string') return raw.trim();

  // 3. Object form — try every known street-key spelling.
  if (raw && typeof raw === 'object') {
    let street = '';
    if (typeof raw.line1 === 'string' && raw.line1.trim()) {
      street = raw.line1.trim();
    } else if (typeof raw.street === 'string' && raw.street.trim()) {
      street = raw.street.trim();
    } else if (raw.streetNumber || raw.streetName) {
      street = [raw.streetNumber, raw.streetName].filter(Boolean).join(' ').trim();
    }

    const cityStateZip = [raw.city, raw.state, raw.zip]
      .map((p) => (typeof p === 'string' ? p.trim() : ''))
      .filter(Boolean)
      .join(', ');

    return [street, cityStateZip].filter(Boolean).join(', ');
  }

  return '';
}
